import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      if (data.access_token) {
        api.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
      }
      setUser(data);
      toast.success(`Welcome, ${data.name}`);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative">
        <img src={`${process.env.PUBLIC_URL}/assets/hero/auth-hero.jpg`} alt="Fashion model" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <p className="text-xs uppercase tracking-[0.25em] mb-3 opacity-80">Lumina AI</p>
          <p className="font-heading text-3xl font-semibold leading-snug">From first sketch to final lookbook — before a single sample is made.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="font-heading font-bold text-xl tracking-tight" data-testid="auth-logo">LUMINA<span className="text-zinc-400">.AI</span></Link>
          <h1 className="font-heading text-3xl font-bold mt-10 tracking-tight">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-zinc-500 mt-2">{mode === "login" ? "Log in to your team workspace." : "Join your team's design workspace."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Full name</label>
                <input data-testid="auth-name-input" required value={form.name} onChange={set("name")}
                  className="mt-1.5 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" placeholder="Ava Laurent" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Email</label>
              <input data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")}
                className="mt-1.5 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" placeholder="you@brand.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Password</label>
              <input data-testid="auth-password-input" type="password" required value={form.password} onChange={set("password")}
                className="mt-1.5 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" placeholder="••••••••" />
            </div>
            {error && <p data-testid="auth-error-message" className="text-sm text-red-600">{error}</p>}
            <button data-testid="auth-submit-button" disabled={loading}
              className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60">
              {loading ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
            </button>
          </form>

          <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
            {mode === "login" ? "No account? Create one →" : "Already have an account? Log in →"}
          </button>
        </div>
      </div>
    </div>
  );
}
